// =============================================================================
// Origin UI — src/pages/tasks/TaskDetailPage.tsx
// Full task detail with status update, assignment, evidence notes.
// IT Ops can: assign, update status (InProgress/Completed/Blocked), add evidence.
// Admin can: assign only (cannot complete).
// Also shows parent request context, relevant selections, and link.
//
// Sep 2026: "What's Needed" section shows relevant selections from parent request.
// Technology checklist defaults unchecked (completion tracker, not inclusion indicator).
// =============================================================================

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { taskApi, requestApi, referenceApi } from '@/services/requestApi';
import {
  StatusBadge, LoadingSpinner, Card, PageHeader,
  ErrorBanner, ConfirmDialog,
} from '@/components/common';
import type { TaskDetail, RequestDetail, ITStaffRef } from '@/types/api';
import styles from './tasks.module.css';

export function TaskDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [task, setTask] = useState<TaskDetail | null>(null);
  const [parentRequest, setParentRequest] = useState<RequestDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Status update state
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [targetStatus, setTargetStatus] = useState('');
  const [evidenceNotes, setEvidenceNotes] = useState('');
  const [statusError, setStatusError] = useState('');

  // Assign state
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assigneeId, setAssigneeId] = useState('');
  const [assigneeName, setAssigneeName] = useState('');
  const [itStaffList, setITStaffList] = useState<ITStaffRef[]>([]);

  // Reopen state (shown on parent request if Completed)
  const [reopenDialogOpen, setReopenDialogOpen] = useState(false);
  const [reopenReason, setReopenReason] = useState('');

  // Technology checklist — defaults UNCHECKED. IT ticks items as they complete them.
  const TECH_ITEMS = ['Fingerprint Access', 'Printer Access', 'AV', 'Office 365 License', 'Scanner Address Book', 'Added to Default DLs'];
  const [techChecks, setTechChecks] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {};
    TECH_ITEMS.forEach(item => { init[item] = false; });
    return init;
  });

  useEffect(() => {
    if (id) loadTask(id);
  }, [id]);

  async function loadTask(taskId: string) {
    try {
      setLoading(true);
      const taskData = await taskApi.getById(taskId);
      setTask(taskData);
      // Also load parent request for context
      try {
        const reqData = await requestApi.getById(taskData.requestId);
        setParentRequest(reqData);
      } catch { /* parent request load is best-effort */ }
      // Load IT staff for assignment dropdown
      try {
        const staff = await referenceApi.itStaff();
        setITStaffList(staff);
      } catch { /* IT staff load is best-effort */ }
    } catch (err: any) {
      setError(err.message || 'Failed to load task');
    } finally {
      setLoading(false);
    }
  }

  function openStatusDialog(status: string) {
    setTargetStatus(status);
    setEvidenceNotes(task?.evidenceNotes || '');
    setStatusError('');
    setStatusDialogOpen(true);
  }

  async function handleStatusUpdate() {
    if (!id || !task) return;

    // Validate evidence notes — required only for Blocked (optional for Completed)
    if (targetStatus === 'Blocked' && !evidenceNotes.trim()) {
      setStatusError('A reason is required when marking a task as blocked.');
      return;
    }

    try {
      setSubmitting(true);
      setStatusError('');
      let finalNotes = evidenceNotes.trim() || '';
      // Append technology checklist summary when completing the Configure Standard Technology task
      if (targetStatus === 'Completed' && task.taskType === 'Configure Standard Technology') {
        const techSummary = TECH_ITEMS
          .map(item => `${techChecks[item] ? '✓' : '✗'} ${item}`)
          .join(', ');
        finalNotes = finalNotes ? `${finalNotes}\n\nTechnology: ${techSummary}` : `Technology: ${techSummary}`;
      }
      await taskApi.updateStatus(id, targetStatus, finalNotes || (targetStatus === 'Completed' ? 'Task completed.' : undefined));
      setStatusDialogOpen(false);
      await loadTask(id);
    } catch (err: any) {
      const body = err.body ? JSON.parse(err.body) : null;
      setStatusError(body?.error || 'Failed to update status');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleAssign() {
    if (!id || !assigneeId.trim() || !assigneeName.trim()) return;
    try {
      setSubmitting(true);
      await taskApi.assign(id, assigneeId.trim(), assigneeName.trim());
      setAssignDialogOpen(false);
      await loadTask(id);
    } catch (err: any) {
      const body = err.body ? JSON.parse(err.body) : null;
      setError(body?.error || 'Failed to assign task');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleReopen() {
    if (!parentRequest || !reopenReason.trim()) return;
    try {
      setSubmitting(true);
      await taskApi.reopen(parentRequest.requestId, reopenReason.trim());
      setReopenDialogOpen(false);
      await loadTask(id!);
    } catch (err: any) {
      const body = err.body ? JSON.parse(err.body) : null;
      setError(body?.error || 'Failed to reopen request');
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) return <LoadingSpinner />;
  if (!task) return <ErrorBanner message="Task not found" />;

  const t = task;

  // Determine available status transitions for the action buttons
  const statusActions: { label: string; status: string; variant: string }[] = [];
  if (t.actions.canUpdateStatus) {
    if (t.status === 'Pending') {
      statusActions.push({ label: 'Start Task', status: 'InProgress', variant: 'primary' });
      statusActions.push({ label: 'Complete', status: 'Completed', variant: 'success' });
    }
    if (t.status === 'InProgress') {
      statusActions.push({ label: 'Complete', status: 'Completed', variant: 'success' });
      statusActions.push({ label: 'Mark Blocked', status: 'Blocked', variant: 'danger' });
    }
    if (t.status === 'Blocked') {
      statusActions.push({ label: 'Unblock (In Progress)', status: 'InProgress', variant: 'primary' });
    }
  }

  // ── Extract relevant selections from parent request for "What's Needed" ──
  const getSelections = (type: string) => parentRequest?.selections.filter(s => s.selectionType === type).map(s => s.selectionValue) ?? [];
  const getRemark = (type: string) => parentRequest?.selections.find(s => s.selectionType === type)?.selectionValue;
  const tagStyle = { display: 'inline-block' as const, padding: '3px 10px', fontSize: 12, fontWeight: 500, borderRadius: 4, backgroundColor: '#F3F4F6', color: '#374151', marginRight: 6, marginBottom: 4 };
  const noteStyle = { fontSize: 13, color: '#6B7280', margin: '4px 0 0' };
  const sectionHeadingStyle = { fontSize: 12, fontWeight: 700 as const, color: '#0A2240', marginBottom: 4, marginTop: 10, textTransform: 'uppercase' as const, letterSpacing: '0.5px' };

  return (
    <div>
      <PageHeader
        title={t.taskType}
        subtitle={t.description || undefined}
        action={<StatusBadge status={t.status} />}
      />

      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}

      {/* Status Action Bar */}
      {(statusActions.length > 0 || t.actions.canAssign) && (
        <div className={styles.actionBar}>
          {statusActions.map(sa => (
            <button
              key={sa.status}
              className={
                sa.variant === 'success' ? styles.btnSuccess
                  : sa.variant === 'danger' ? styles.btnDanger
                  : styles.btnPrimary
              }
              onClick={() => openStatusDialog(sa.status)}
              disabled={submitting}
            >
              {sa.label}
            </button>
          ))}
          {t.actions.canAssign && (
            <button
              className={styles.btnOutline}
              onClick={() => setAssignDialogOpen(true)}
              disabled={submitting}
            >
              {t.assignedTo ? 'Reassign' : 'Assign'}
            </button>
          )}
        </div>
      )}

      <div className={styles.detailLayout}>
        {/* Main content */}
        <div className={styles.detailMain}>
          {/* Task Info */}
          <Card className={styles.section}>
            <h3 className={styles.sectionTitle}>Task Details</h3>
            <div className={styles.detailGrid}>
              <DetailField label="Task Type" value={t.taskType} />
              <DetailField label="Status" value={t.status === 'InProgress' ? 'In Progress' : t.status} />
              <DetailField label="Assigned To" value={t.assignedToDisplayName || 'Unassigned'} />
              <DetailField label="Created" value={new Date(t.createdAt).toLocaleString()} />
              {t.completedAt && (
                <DetailField label="Completed" value={new Date(t.completedAt).toLocaleString()} />
              )}
              {t.completedBy && (
                <DetailField label="Completed By" value={t.completedBy} />
              )}
            </div>
            {t.description && (
              <div className={styles.descriptionBox}>
                <span className={styles.descLabel}>Description</span>
                <p className={styles.descText}>{t.description}</p>
              </div>
            )}
            {t.evidenceNotes && (
              <div className={styles.evidenceBox}>
                <span className={styles.descLabel}>Evidence / Notes</span>
                <p className={styles.descText}>{t.evidenceNotes}</p>
              </div>
            )}
          </Card>

          {/* What's Needed — show relevant selections from the parent request */}
          {parentRequest && (() => {
            const hwItems = getSelections('Hardware');
            const swItems = getSelections('Software');
            const hasCitrix = parentRequest.selections.some(s => s.selectionType === 'Citrix');
            const folderItems = getSelections('FolderAccess');
            const swRemarks = getRemark('SoftwareRemarks');
            const hwRemarks = getRemark('HardwareRemarks');
            const iPhoneType = getRemark('iPhoneType');
            const mobilePackage = getRemark('MobilePackage');
            const appBatch = getRemark('AppBatchDistribution');
            const emailDist = getRemark('EmailDistributionLists');
            const otherAccess = getRemark('OtherAccess');
            const hasAnything = hwItems.length > 0 || swItems.length > 0 || hasCitrix || folderItems.length > 0 || appBatch || emailDist || otherAccess;

            if (!hasAnything) return null;

            return (
              <Card className={styles.section}>
                <h3 className={styles.sectionTitle}>What&apos;s Needed</h3>
                <p style={{ fontSize: 13, color: '#6B7280', marginBottom: 10 }}>
                  Requirements from the onboarding request for <strong>{parentRequest.fullName}</strong>
                </p>

                {hwItems.length > 0 && (
                  <div>
                    <div style={sectionHeadingStyle}>Hardware</div>
                    <div>{hwItems.map(item => <span key={item} style={tagStyle}>{item}</span>)}</div>
                    {hasCitrix && <span style={{ ...tagStyle, backgroundColor: '#EEF2FF', color: '#4338CA' }}>Citrix Access</span>}
                    {iPhoneType && <p style={noteStyle}>iPhone Type: {iPhoneType}</p>}
                    {mobilePackage && <p style={noteStyle}>Package: {mobilePackage}</p>}
                    {hwRemarks && <p style={noteStyle}>Remarks: {hwRemarks}</p>}
                  </div>
                )}

                {(swItems.length > 0 || swRemarks) && (
                  <div>
                    <div style={sectionHeadingStyle}>Software</div>
                    <div>{swItems.map(item => <span key={item} style={tagStyle}>{item}</span>)}</div>
                    {!hwItems.length && hasCitrix && <span style={{ ...tagStyle, backgroundColor: '#EEF2FF', color: '#4338CA' }}>Citrix Access</span>}
                    {swRemarks && <p style={noteStyle}>Remarks: {swRemarks}</p>}
                  </div>
                )}

                {folderItems.length > 0 && (
                  <div>
                    <div style={sectionHeadingStyle}>Folder Access</div>
                    <div>{folderItems.map(item => <span key={item} style={tagStyle}>{item}</span>)}</div>
                  </div>
                )}

                {(appBatch || emailDist || otherAccess) && (
                  <div>
                    <div style={sectionHeadingStyle}>Distribution &amp; Other</div>
                    {appBatch && <p style={noteStyle}><strong>App/Batch:</strong> {appBatch}</p>}
                    {emailDist && <p style={noteStyle}><strong>Email/DLs:</strong> {emailDist}</p>}
                    {otherAccess && <p style={noteStyle}><strong>Other:</strong> {otherAccess}</p>}
                  </div>
                )}
              </Card>
            );
          })()}

          {/* Technology Checklist — IT ticks items as they complete them */}
          {parentRequest && t.taskType === 'Configure Standard Technology' && (
            <Card className={styles.section}>
              <h3 className={styles.sectionTitle}>
                Technology Checklist
                <span style={{ fontSize: '0.8em', fontWeight: 'normal', color: '#666', marginLeft: 8 }}>
                  Tick items as you complete them
                </span>
              </h3>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
                {TECH_ITEMS.map(item => (
                  <label key={item} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: t.status === 'Completed' ? 'default' : 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={techChecks[item] ?? false}
                      onChange={() => {
                        if (t.status !== 'Completed') {
                          setTechChecks(prev => ({ ...prev, [item]: !prev[item] }));
                        }
                      }}
                      disabled={t.status === 'Completed'}
                    />
                    <span>{item}</span>
                  </label>
                ))}
              </div>
              {t.status !== 'Completed' && (
                <p style={{ fontSize: '0.85em', color: '#888', marginTop: 8 }}>
                  Tick each item as you set it up. Status will be recorded in evidence notes when the task is completed.
                </p>
              )}
            </Card>
          )}

          {/* Parent Request Context */}
          {parentRequest && (
            <Card className={styles.section}>
              <h3 className={styles.sectionTitle}>Parent Request</h3>
              <div className={styles.detailGrid}>
                <DetailField label="Employee" value={parentRequest.fullName} />
                <DetailField label="Type" value={parentRequest.requestType} />
                <DetailField label="Status" value={parentRequest.status === 'TasksInProgress' ? 'Tasks In Progress' : parentRequest.status} />
                <DetailField label="Department" value={parentRequest.department} />
                <DetailField label="Office" value={parentRequest.officeLocation} />
                <DetailField label="Effective Date" value={new Date(parentRequest.effectiveDate).toLocaleDateString()} />
              </div>
              <div className={styles.parentActions}>
                <Link to={`/requests/${parentRequest.requestId}`} className={styles.btnGhost}>
                  View Full Request →
                </Link>
                {parentRequest.actions.canReopen && (
                  <button
                    className={styles.btnOutline}
                    onClick={() => setReopenDialogOpen(true)}
                  >
                    Reopen Request
                  </button>
                )}
              </div>
            </Card>
          )}

          {/* Other tasks for same request */}
          {parentRequest && parentRequest.tasks.length > 1 && (
            <Card className={styles.section}>
              <h3 className={styles.sectionTitle}>
                All Tasks for This Request ({parentRequest.tasks.length})
              </h3>
              <div className={styles.siblingTasks}>
                {parentRequest.tasks.map(st => (
                  <div
                    key={st.taskId}
                    className={`${styles.siblingTaskRow} ${st.taskId === t.taskId ? styles.siblingCurrent : ''}`}
                    onClick={() => st.taskId !== t.taskId && navigate(`/tasks/${st.taskId}`)}
                  >
                    <span className={styles.siblingType}>{st.taskType}</span>
                    <StatusBadge status={st.status} />
                    {st.assignedToDisplayName && (
                      <span className={styles.muted}>{st.assignedToDisplayName}</span>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className={styles.detailSidebar}>
          <Card>
            <h4 className={styles.sidebarTitle}>Quick Info</h4>
            <div className={styles.sidebarMeta}>
              <div><span>Task ID</span><span className={styles.muted}>{t.taskId.slice(0, 8)}…</span></div>
              <div><span>Request ID</span><span className={styles.muted}>{t.requestId.slice(0, 8)}…</span></div>
              <div><span>Status</span><StatusBadge status={t.status} /></div>
              {parentRequest && (
                <>
                  <div><span>Employee</span><span>{parentRequest.fullName}</span></div>
                  <div><span>Request Status</span><StatusBadge status={parentRequest.status} /></div>
                </>
              )}
            </div>
          </Card>
        </div>
      </div>

      {/* ─── Status Update Dialog ─── */}
      {statusDialogOpen && (
        <div className={styles.overlay} onClick={() => setStatusDialogOpen(false)}>
          <div className={styles.dialog} onClick={e => e.stopPropagation()}>
            <h3 className={styles.dialogTitle}>
              {targetStatus === 'Completed' ? 'Complete Task'
                : targetStatus === 'Blocked' ? 'Mark as Blocked'
                : targetStatus === 'InProgress' ? 'Start Task'
                : 'Update Status'}
            </h3>
            <p className={styles.dialogMessage}>
              {targetStatus === 'Completed'
                ? 'Optionally provide evidence notes describing what was done.'
                : targetStatus === 'Blocked'
                ? 'Please explain why this task is blocked.'
                : `Change task status to ${targetStatus === 'InProgress' ? 'In Progress' : targetStatus}.`}
            </p>
            {statusError && <div className={styles.dialogError}>{statusError}</div>}
            <div className={styles.dialogField}>
              <label className={styles.dialogLabel}>
                {targetStatus === 'Completed' ? 'Evidence Notes' : targetStatus === 'Blocked' ? 'Reason' : 'Notes (optional)'}
                {targetStatus === 'Blocked' && (
                  <span className={styles.required}> *</span>
                )}
              </label>
              <textarea
                className={`${styles.dialogTextarea} ${statusError ? styles.dialogTextareaError : ''}`}
                value={evidenceNotes}
                onChange={e => { setEvidenceNotes(e.target.value); setStatusError(''); }}
                placeholder={
                  targetStatus === 'Completed' ? 'Describe what was completed...'
                    : targetStatus === 'Blocked' ? 'Why is this task blocked?'
                    : 'Optional notes...'
                }
                rows={4}
                autoFocus
              />
            </div>
            <div className={styles.dialogActions}>
              <button className={styles.btnSecondary} onClick={() => setStatusDialogOpen(false)}>
                Cancel
              </button>
              <button
                className={targetStatus === 'Completed' ? styles.btnSuccess : targetStatus === 'Blocked' ? styles.btnDanger : styles.btnPrimary}
                onClick={handleStatusUpdate}
                disabled={submitting}
              >
                {submitting ? 'Updating...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Assign Dialog ─── */}
      <ConfirmDialog
        open={assignDialogOpen}
        title={t.assignedTo ? 'Reassign Task' : 'Assign Task'}
        message="Select the IT staff member to assign this task to."
        confirmLabel={t.assignedTo ? 'Reassign' : 'Assign'}
        confirmVariant="primary"
        onConfirm={handleAssign}
        onCancel={() => setAssignDialogOpen(false)}
      >
        <div className={styles.dialogField}>
          <label className={styles.dialogLabel}>IT Staff Member</label>
          <select
            className={styles.dialogInput}
            value={assigneeId}
            onChange={e => {
              const selected = itStaffList.find(s => s.entraObjectId === e.target.value);
              if (selected) {
                setAssigneeId(selected.entraObjectId);
                setAssigneeName(selected.fullName);
              }
            }}
            style={{ width: '100%', padding: '8px', fontSize: '14px' }}
          >
            <option value="">Select staff member...</option>
            {itStaffList.map(s => (
              <option key={s.id} value={s.entraObjectId}>{s.fullName} ({s.email})</option>
            ))}
          </select>
        </div>
      </ConfirmDialog>

      {/* ─── Reopen Dialog ─── */}
      {reopenDialogOpen && (
        <div className={styles.overlay} onClick={() => setReopenDialogOpen(false)}>
          <div className={styles.dialog} onClick={e => e.stopPropagation()}>
            <h3 className={styles.dialogTitle}>Reopen Request</h3>
            <p className={styles.dialogMessage}>
              This will transition the request from Completed back to Tasks In Progress.
              A reason is required.
            </p>
            <div className={styles.dialogField}>
              <label className={styles.dialogLabel}>
                Reason <span className={styles.required}>*</span>
              </label>
              <textarea
                className={styles.dialogTextarea}
                value={reopenReason}
                onChange={e => setReopenReason(e.target.value)}
                placeholder="Why does this request need to be reopened?"
                rows={3}
                autoFocus
              />
            </div>
            <div className={styles.dialogActions}>
              <button className={styles.btnSecondary} onClick={() => setReopenDialogOpen(false)}>
                Cancel
              </button>
              <button
                className={styles.btnPrimary}
                onClick={handleReopen}
                disabled={submitting || !reopenReason.trim()}
              >
                {submitting ? 'Reopening...' : 'Reopen Request'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DetailField({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.detailFieldItem}>
      <span className={styles.detailLabel}>{label}</span>
      <span className={styles.detailValue}>{value}</span>
    </div>
  );
}
